import {Component, inject, Input, OnInit} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {JsonPipe} from '@angular/common';
import {MaterialModule} from '../../../../material.module';
import {IProductFeedOptions} from '../../../../models/product-feed-options.model';
import {IAssistantOptions} from '../../../../models/assistant-options.model';
import {IChatbotOptions} from '../../../../models/chatbot-options.model';
import {AssistantsService} from '../../../../services/objects/assistants.service';
import {ChatbotsService} from '../../../../services/objects/chatbots.service';
import {ProductLookupDiagnosticsService} from '../../../../services/product-lookup-diagnostics.service';
import {
  ProductLookupPlanDiagnostic,
  ProductLookupPlanningDiagnosticResponse
} from '../../../../models/product-lookup-diagnostic.model';

@Component({
  selector: 'app-product-lookup-diagnostic',
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule, JsonPipe],
  templateUrl: './product-lookup-diagnostic.component.html',
  styleUrls: ['./product-lookup-diagnostic.component.scss']
})
export class ProductLookupDiagnosticComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly assistantsService = inject(AssistantsService);
  private readonly chatbotsService = inject(ChatbotsService);
  private readonly diagnosticsService = inject(ProductLookupDiagnosticsService);

  @Input({required: true}) productFeed!: IProductFeedOptions;

  readonly form = this.fb.group({
    chatbotId: ['', Validators.required],
    assistantId: ['', Validators.required],
    needs: ['', [Validators.required, Validators.maxLength(512)]],
    pageUrl: ['', Validators.maxLength(2048)],
    market: ['', Validators.maxLength(64)],
    limit: [5, [Validators.required, Validators.min(1), Validators.max(10)]]
  });

  chatbots: IChatbotOptions[] = [];
  assistants: IAssistantOptions[] = [];
  loadingConfiguration = true;
  running = false;
  configurationError = false;
  result: ProductLookupPlanningDiagnosticResponse | null = null;
  requestFailed = false;

  ngOnInit(): void {
    this.form.get('market')?.setValue(this.productFeed.market ?? '');
    forkJoin({
      chatbots: this.chatbotsService.getAllAsync(),
      assistants: this.assistantsService.getAllAsync()
    }).subscribe({
      next: ({chatbots, assistants}) => {
        this.chatbots = (chatbots ?? []).filter(chatbot => chatbot.enabled);
        this.assistants = (assistants ?? []).filter(assistant => assistant.enabled);
        this.selectFirstConfiguredPair();
        this.loadingConfiguration = false;
      },
      error: () => {
        this.configurationError = true;
        this.loadingConfiguration = false;
      }
    });

    this.form.get('chatbotId')?.valueChanges.subscribe(() => this.ensureConfiguredAssistant());
  }

  get assistantsForSelectedChatbot(): IAssistantOptions[] {
    const chatbot = this.chatbots.find(item => item.id === this.form.get('chatbotId')?.value);
    const refs = new Set((chatbot?.assistantRefs ?? []).filter(Boolean));
    return this.assistants
      .filter(assistant => refs.has(assistant.id) || refs.has(assistant.assistantId))
      .filter(assistant => (assistant.productFeedRefs ?? []).includes(this.productFeed.id));
  }

  get configuredChatbots(): IChatbotOptions[] {
    return this.chatbots.filter(chatbot => {
      const refs = new Set((chatbot.assistantRefs ?? []).filter(Boolean));
      return this.assistants.some(assistant =>
        (refs.has(assistant.id) || refs.has(assistant.assistantId)) &&
        (assistant.productFeedRefs ?? []).includes(this.productFeed.id));
    });
  }

  run(): void {
    if (this.form.invalid || this.running) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    this.running = true;
    this.requestFailed = false;
    this.result = null;
    this.diagnosticsService.planQuery({
      chatbotId: raw.chatbotId!,
      assistantId: raw.assistantId!,
      needs: raw.needs!.trim(),
      pageUrl: raw.pageUrl?.trim() || undefined,
      market: raw.market?.trim() || undefined,
      limit: raw.limit ?? 5
    }).subscribe({
      next: result => {
        this.result = result;
        this.running = false;
      },
      error: () => {
        this.requestFailed = true;
        this.running = false;
      }
    });
  }

  planClauseCount(plan: ProductLookupPlanDiagnostic | null | undefined): number {
    if (!plan) return 0;
    return plan.text.length + plan.exactFilters.length + plan.int32Ranges.length + plan.categories.length;
  }

  private selectFirstConfiguredPair(): void {
    const chatbot = this.configuredChatbots[0];
    if (!chatbot) return;
    this.form.get('chatbotId')?.setValue(chatbot.id);
    this.ensureConfiguredAssistant();
  }

  private ensureConfiguredAssistant(): void {
    const assistants = this.assistantsForSelectedChatbot;
    const selected = String(this.form.get('assistantId')?.value ?? '');
    if (!assistants.some(assistant => assistant.id === selected || assistant.assistantId === selected)) {
      this.form.get('assistantId')?.setValue(assistants[0]?.id ?? '', {emitEvent: false});
    }
  }
}
